// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the value under the controller's ray, shown where the ray meets
 * the globe (the AR refresh, 2026-10-04). The first VR version printed
 * it under the dataset title in the HUD, half a metre from the place it
 * described; now it sits beside the laser dot, with the place's
 * coordinates under it, the in-headset counterpart of the browser's
 * pointer readout. Only on a dataset with values, only with a plan that
 * has the data tools (vrSession's probe checks both), and never on a
 * phone, which reads values with pins.
 */

import type * as THREE from 'three'
import { createCanvasPanel, PANEL_COLORS } from './vrPanelKit'

const PANEL_W = 0.2
const PANEL_H = 0.072
const CANVAS_W = 500
const CANVAS_H = 180

export interface VrReadoutHandle {
  readonly mesh: THREE.Mesh
  /** What to show; null hides it. Redraws only on change. */
  set(value: string | null, where: string | null): void
  /** Put it beside a point on the globe, facing the viewer. */
  place(point: THREE.Vector3, camera: THREE.Vector3): void
  dispose(): void
}

/** Degrees as the browser's readout prints them: "12.3° N, 45.1° W". */
export function formatLatLon(lat: number, lon: number): string {
  const ns = lat >= 0 ? 'N' : 'S'
  const ew = lon >= 0 ? 'E' : 'W'
  return `${Math.abs(lat).toFixed(1)}° ${ns}, ${Math.abs(lon).toFixed(1)}° ${ew}` // i18n-exempt: compass letters and degrees, as the 2D readout
}

export function createVrReadout(THREE_: typeof THREE): VrReadoutHandle {
  const panel = createCanvasPanel<never>(THREE_, {
    widthM: PANEL_W,
    heightM: PANEL_H,
    canvasW: CANVAS_W,
    canvasH: CANVAS_H,
    renderOrder: 13,
    // It rides the ray's tip: not a panel to lay out (vrPanelLayout).
    layout: 'free',
  })
  panel.mesh.visible = false
  let drawn = ''
  const toCamera = new THREE_.Vector3()
  const up = new THREE_.Vector3(0, 1, 0)

  return {
    mesh: panel.mesh,
    set(value, where) {
      const key = value ? `${value}|${where ?? ''}` : ''
      panel.mesh.visible = key !== ''
      if (key === drawn) return
      drawn = key
      if (!value) return
      panel.draw((p) => {
        p.g.clearRect(0, 0, CANVAS_W, CANVAS_H)
        p.box(2, 2, CANVAS_W - 4, CANVAS_H - 4, 22, 'rgba(13, 13, 18, 0.9)', 'rgba(74, 222, 128, 0.8)', 3)
        p.text(value, 28, where ? 62 : CANVAS_H / 2, { size: 52, weight: 700, color: '#4ade80', maxWidth: CANVAS_W - 56 })
        if (where) p.text(where, 28, 132, { size: 30, color: PANEL_COLORS.textSecondary, maxWidth: CANVAS_W - 56 })
      })
    },
    place(point, camera) {
      // A little toward the viewer and up-right of the dot, so the dot stays visible.
      toCamera.copy(camera).sub(point).normalize()
      panel.mesh.position.copy(point)
        .addScaledVector(toCamera, 0.05)
        .addScaledVector(up, PANEL_H * 0.9)
      panel.mesh.lookAt(camera)
      panel.mesh.translateX(PANEL_W * 0.55)
    },
    dispose() {
      panel.dispose()
    },
  }
}
